/**
 * @fileoverview Event Logger Provider
 * 
 * Automatically logs page views and provides event logging context.
 * Wrap your app with this provider to enable automatic page tracking.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useEventLogger } from '@/hooks/useEventLogger';

interface EventLoggerProviderProps {
  children: React.ReactNode;
}

// Map routes to friendly page names
const getPageName = (pathname: string): string => {
  const routeMap: Record<string, string> = {
    '/': 'saves',
    '/vote': 'community_feed',
    '/try-on': 'try_on',
    '/chat': 'ai_chat',
    '/notifications': 'notifications',
    '/settings': 'settings',
    '/profile': 'profile',
    '/pricing': 'pricing',
    '/style-profile': 'style_profile',
    '/celebrity-match': 'celebrity_match',
    '/body-gym': 'body_gym',
    '/clean-out': 'clean_out',
    '/purchases': 'purchases',
    '/price-alerts': 'price_alerts',
    '/whats-new': 'whats_new',
    '/safari-extension': 'safari_extension',
    '/chrome-extension': 'chrome_extension',
    
  };

  // Check exact match first
  if (routeMap[pathname]) return routeMap[pathname];

  // Check dynamic routes
  if (pathname.startsWith('/saved/')) return 'item_detail';
  if (pathname.startsWith('/collection/')) return 'collection_detail';
  if (pathname.startsWith('/user/')) return 'user_profile';
  if (pathname.startsWith('/admin')) return 'admin_' + pathname.split('/')[2] || 'dashboard';

  return 'unknown';
};

export function EventLoggerProvider({ children }: EventLoggerProviderProps) {
  const location = useLocation();
  const { logPageView } = useEventLogger();
  const previousPathRef = useRef<string | null>(null);

  // Log page views on route change
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Avoid duplicate logs for the same path
    if (previousPathRef.current === currentPath) return;
    previousPathRef.current = currentPath;

    const pageName = getPageName(currentPath);
    logPageView(pageName, {
      path: currentPath,
      search: location.search,
      referrer: document.referrer || null,
    });
  }, [location.pathname, location.search, logPageView]);

  return <>{children}</>;
}
